import {Component, inject, OnInit, ViewChild} from '@angular/core';
import {DatePipe} from '@angular/common';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatAccordion} from "@angular/material/expansion";
import {AssistantsService} from "../../../../services/objects/assistants.service";
import {ClientService} from "../../../../services/auth/client.service";
import {MatDialog} from "@angular/material/dialog";
import {ToastService} from "../../../../services/layout/toast.service";
import {AssistantRegressionTestCase, IAssistantOptions} from "../../../../models/assistant-options.model";
import {
    AssistantFeedBindingWarning,
    AssistantVersionOptions,
    UpdateAssistantDraftRequest
} from "../../../../models/assistant-version.model";
import {MaterialModule} from "../../../../material.module";
import {ShineAuthDomainsService} from "../../../../services/auth/shine-auth/shine-auth-domains.service";

import {ListingsComponent} from "../../../objects/listings/listings.component";
import {ConfirmDialogComponent} from "../../../confirm-dialog/confirm-dialog.component";
import {AssistantGeneralSettingsComponent} from "../assistant-general-settings/assistant-general-settings.component";
import {Marked} from "marked";
import {AssistantReportComponent} from "../../reports/assistant-report.component";
import {AssistantMetrics} from "../../reports/assistant-metrics.model";
import {TabbedPageComponent} from "../../../objects/tabbed-page/tabbed-page.component";
import {ClientObjectSettings} from "../../../objects/client-object-settings/client-object-settings.component";
import {
    getReasoningEffortDefault,
    getReasoningEffortOptions,
    isReasoningModel
} from "../../../../utils/reasoning.utils";
import {
    AssistantFeedBindingWarningsComponent
} from "../assistant-feed-binding-warnings/assistant-feed-binding-warnings.component";
import {
    AssistantFunctionManifestPreviewComponent
} from "../assistant-function-manifest-preview/assistant-function-manifest-preview.component";

@Component({
  selector: 'app-assistant-settings',
  imports: [
    MaterialModule,
    ReactiveFormsModule,
    ListingsComponent,
    AssistantGeneralSettingsComponent,
    AssistantReportComponent,
    ClientObjectSettings,
    FormsModule,
    DatePipe,
    AssistantFeedBindingWarningsComponent,
    AssistantFunctionManifestPreviewComponent
  ],
  providers: [Marked],
  templateUrl: './assistant-settings.component.html',
  styleUrl: './assistant-settings.component.scss'
})
export class AssistantSettingsComponent extends TabbedPageComponent implements OnInit{
    private fb = inject(FormBuilder);
    private assistantsService = inject(AssistantsService);
    clientService = inject(ClientService);
    private dialog = inject(MatDialog);
    private snackBar = inject(ToastService);
    private domainsService = inject(ShineAuthDomainsService);

  // State Variables
  isSaving: boolean = false;
  isDeleting: boolean = false;
  isProposing = false;
  isVersionAction = false;
  isSavingRegressionTests = false;
  isFeedBindingValidationLoading = false;

  // Assistant Data
  assistant: IAssistantOptions | null = null;
  id: string = '';

  files: string[] = [];
  functions: string[] = [];
  productFeeds: string[] = [];

  // Per-tab file selections
  directFileRefs: string[] = [];
  vectorFileRefs: string[] = [];

  // Reactive Forms
  settingsForm: FormGroup;
  refsChanged: boolean = false;
  verifiedDomains: string[] = [];
  isDomainsLoading: boolean = false;

  @ViewChild(MatAccordion) accordion: MatAccordion;
  @ViewChild(AssistantFunctionManifestPreviewComponent)
  functionManifestPreview?: AssistantFunctionManifestPreviewComponent;
  reportData: AssistantMetrics[] = [];
  assistantVersions: AssistantVersionOptions[] = [];
  activeDraft: AssistantVersionOptions | null = null;
  regressionTestCases: AssistantRegressionTestCase[] = [];
  feedBindingWarnings: AssistantFeedBindingWarning[] = [];

  get showActionButtons(): boolean {
    return this.selectedIndex !== 1;
  }


  ngOnInit(): void {
    super.OnInit();
    this.settingsForm = this.fb.group({
      realTimeEnabled: [false],
      realTimeVoice: [''],
      model: ['', Validators.required],
      instructions: ['', Validators.required],
      codeInterpreter: [false],
      webSearch: [false],
      webSearchDomains: [[]],
        usePromptCaching: [false],
      temperature: [0.3],
        reasoningEffort: [getReasoningEffortDefault(null)],
        reasoningSummaryVerbosity: [''],
    });

    this.route.params.subscribe(params => {
      this.id = params['id'];
      this.loadData();
    });
  }

  onDirectFileRefsChange(updatedRefs: string[]): void {
    this.directFileRefs = updatedRefs;
    // Ensure mutual exclusivity by removing from vector selection
    const set = new Set(this.directFileRefs);
    this.vectorFileRefs = (this.vectorFileRefs || []).filter(id => !set.has(id));
    this.refsChanged = true;
  }

  onVectorFileRefsChange(updatedRefs: string[]): void {
    this.vectorFileRefs = updatedRefs;
    // Ensure mutual exclusivity by removing from direct selection
    const set = new Set(this.vectorFileRefs);
    this.directFileRefs = (this.directFileRefs || []).filter(id => !set.has(id));
    this.refsChanged = true;
  }

  onFunctionRefsChange(updatedRefs: any[]): void {
    this.assistant!.functionRefs = updatedRefs;
    this.refsChanged = true;
  }

  onProductFeedRefsChange(updatedRefs: string[]): void {
    this.assistant!.productFeedRefs = updatedRefs;
    this.refsChanged = true;
    void this.refreshFeedBindingWarnings();
  }

  async loadData() {
    try {
      // Fetch Assistant Data
      const assistantResponse = await this.assistantsService.getByIdAsync(this.id).toPromise();
      this.assistant = assistantResponse as IAssistantOptions;
      this.regressionTestCases = this.cloneRegressionTestCases(this.assistant.regressionTestCases ?? []);

      // Populate Settings Form
      this.settingsForm.patchValue({
        name: this.assistant?.name,
        enabled: this.assistant?.enabled,
        realTimeEnabled: this.assistant?.realTimeEnabled,
        realTimeVoice: this.assistant?.realTimeVoice,
        model: this.assistant?.model,
        description: this.assistant?.description,
        instructions: this.assistant?.instructions,
        codeInterpreter: this.assistant?.codeInterpreter,
        webSearch: this.assistant?.webSearch ?? false,
        webSearchDomains: this.assistant?.webSearchDomains ?? [],
          usePromptCaching: this.assistant?.usePromptCaching ?? false,
        temperature: this.assistant?.temperature,
          reasoningEffort: this.assistant?.reasoningEffort ?? getReasoningEffortDefault(this.assistant?.model),
          reasoningSummaryVerbosity: this.assistant?.reasoningSummaryVerbosity ?? '',
      });

      // Initialize per-tab selections
      // 1) Prefer per-tab selections persisted on the assistant object (if available)
      // Use either array independently if present; default the missing one to []
      if ((this.assistant?.directFileRefs?.length || this.assistant?.vectorFileRefs?.length)) {
        this.directFileRefs = [...(this.assistant?.directFileRefs ?? [])];
        this.vectorFileRefs = [...(this.assistant?.vectorFileRefs ?? [])];
      } else {
        // Legacy FileRefs represented retrieval-backed knowledge. Keep them in Vector Search instead of allowing
        // browser-local state to silently classify server configuration as Direct Search.
        this.directFileRefs = [];
        this.vectorFileRefs = [...(this.assistant?.fileRefs ?? [])];
      }

      // Ensure mutual exclusivity just in case
      const vectorSet = new Set(this.vectorFileRefs);
      this.directFileRefs = (this.directFileRefs || []).filter(id => !vectorSet.has(id));

      // Fetch Assistants and Master Threads
      this.AddFiles();
      this.AddFunctions();
      this.AddProductFeeds();
      await this.refreshFeedBindingWarnings();

      await this.loadVerifiedDomains();
      await this.loadRegressionTests();

      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 7);
      const endDate = new Date();

      this.assistantsService.reportAsync(this.assistant?.id ?? "", startDate.toISOString(), endDate.toISOString()).subscribe(data => {
        this.reportData = Array.isArray(data) ? data : [];
      });

      await this.loadVersions();

    } catch (error) {
      console.error('Error loading data', error);
      this.snackBar.showError('Error loading data', 3000);
    }
  }

  private async loadVersions(): Promise<void> {
    if (!this.assistant?.id) return;
    try {
      const versions = await this.assistantsService.getVersionsAsync(this.assistant.id).toPromise();
      this.assistantVersions = versions ?? [];
      this.activeDraft = this.assistantVersions.find(version => version.status === 'draft') ?? null;
    } catch (error) {
      console.error('Error loading assistant versions', error);
      this.assistantVersions = [];
      this.activeDraft = null;
    }
  }

  private async loadRegressionTests(): Promise<void> {
    if (!this.assistant?.id) return;
    try {
      const testCases = await this.assistantsService.getRegressionTestsAsync(this.assistant.id).toPromise();
      this.regressionTestCases = this.cloneRegressionTestCases(testCases ?? []);
      this.assistant.regressionTestCases = this.cloneRegressionTestCases(this.regressionTestCases);
    } catch (error) {
      console.error('Error loading assistant regression tests', error);
      this.regressionTestCases = this.cloneRegressionTestCases(this.assistant.regressionTestCases ?? []);
    }
  }

  AddFiles() {
    this.files = this.assistant?.fileRefs ?? [];
  }

  AddFunctions() {
    this.functions = this.assistant?.functionRefs ?? [];
  }

  AddProductFeeds() {
    this.productFeeds = this.assistant?.productFeedRefs ?? [];
  }

  // Save Assistant Settings
  save() {
    if (!this.assistant) return;

    if (this.settingsForm.invalid) {
        this.snackBar.showError('Please fill in all required fields', 3000);
        return;
    }

    this.isSaving = true;

    // Merge form values
    Object.assign(this.assistant, this.settingsForm.value);
      this.normalizeReasoningOptions();
      this.assistant.webSearchDomains = this.normalizeDomainList(this.assistant.webSearchDomains ?? []);
      this.assistant.regressionTestCases = this.normalizeRegressionTestCases(this.regressionTestCases);

    // Persist union of selections for backward compatibility to backend
    const union = this.getUnionFileRefs(this.directFileRefs, this.vectorFileRefs);
    this.assistant.fileRefs = union;

    // Also persist per-tab selections on the assistant object (if backend stores extra fields)
    this.assistant.directFileRefs = [...(this.directFileRefs ?? [])];
    this.assistant.vectorFileRefs = [...(this.vectorFileRefs ?? [])];

    this.assistant.updatedDate = new Date();
    void this.refreshFeedBindingWarnings();
    // Send Save Request
    this.assistantsService.saveAsync(this.assistant).subscribe(() => {
      this.snackBar.showSuccess('Settings saved successfully', 3000);
      this.isSaving = false;
      this.functionManifestPreview?.refresh();
    }, error => {
      console.error('Error saving settings', error);
      this.snackBar.showError('Error saving settings', 3000);
      this.isSaving = false;
    });
  }

  async saveDraft(): Promise<void> {
    if (!this.assistant) return;
    if (this.settingsForm.invalid) {
      this.snackBar.showError('Please fill in all required fields', 3000);
      return;
    }

    this.isVersionAction = true;
    try {
      this.applyFormToAssistant();
      const draft = this.activeDraft ?? await this.assistantsService.createDraftAsync(this.assistant.id).toPromise();
      if (!draft?.id) throw new Error('Draft creation failed.');

      const request: UpdateAssistantDraftRequest = {
        instructions: this.assistant.instructions,
        config: this.buildAssistantDraftConfig()
      };
      this.activeDraft = await this.assistantsService.updateDraftAsync(this.assistant.id, draft.id, request).toPromise() ?? null;
      await this.loadVersions();
      await this.refreshFeedBindingWarnings();
      this.settingsForm.markAsPristine();
      this.refsChanged = false;
      this.snackBar.showSuccess('Draft saved and tested.', 3000);
    } catch (error) {
      console.error('Error saving assistant draft', error);
      this.snackBar.showError('Error saving draft.', 3000);
    } finally {
      this.isVersionAction = false;
    }
  }

  async publishDraft(): Promise<void> {
    if (!this.assistant?.id || !this.activeDraft?.id) return;
    this.isVersionAction = true;
    try {
      const response = await this.assistantsService.publishDraftAsync(this.assistant.id, this.activeDraft.id).toPromise();
      if (response?.assistant) {
        this.assistant = response.assistant;
        this.regressionTestCases = this.cloneRegressionTestCases(this.assistant.regressionTestCases ?? []);
        this.settingsForm.patchValue(this.assistant);
      }
      await this.loadVersions();
      await this.refreshFeedBindingWarnings();
      this.functionManifestPreview?.refresh();
      this.snackBar.showSuccess('Draft published.', 3000);
    } catch (error) {
      console.error('Error publishing draft', error);
      this.snackBar.showError('Draft publish failed. Check validation and tests.', 4000);
    } finally {
      this.isVersionAction = false;
    }
  }

  async rollback(version: AssistantVersionOptions): Promise<void> {
    if (!this.assistant?.id || !version?.id) return;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '350px',
      data: {
        title: 'Rollback assistant?',
        message: `This will publish version ${version.id} as the active assistant configuration.`,
        confirmText: 'Do you want to proceed?',
        confirmButtonText: 'Rollback',
        cancelButtonText: 'Cancel'
      }
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (!result || !this.assistant?.id) return;
      this.isVersionAction = true;
      try {
        const response = await this.assistantsService.rollbackAsync(this.assistant.id, version.id).toPromise();
        if (response?.assistant) {
          this.assistant = response.assistant;
          this.regressionTestCases = this.cloneRegressionTestCases(this.assistant.regressionTestCases ?? []);
          this.settingsForm.patchValue(this.assistant);
        }
        await this.loadVersions();
        await this.refreshFeedBindingWarnings();
        this.functionManifestPreview?.refresh();
        this.snackBar.showSuccess('Assistant rolled back.', 3000);
      } catch (error) {
        console.error('Error rolling back assistant', error);
        this.snackBar.showError('Rollback failed.', 3000);
      } finally {
        this.isVersionAction = false;
      }
    });
  }

  canPublishDraft(): boolean {
    return !!this.activeDraft?.validationResult?.passed && !!this.activeDraft?.testRunSummary?.passed;
  }

  addRegressionTestCase(): void {
    this.regressionTestCases = [
      ...this.regressionTestCases,
      {
        name: `Regression ${this.regressionTestCases.length + 1}`,
        expectedInstructionsContains: '',
        forbiddenInstructionsContains: '',
        expectedModel: this.settingsForm.get('model')?.value ?? this.assistant?.model ?? '',
        enabled: true
      }
    ];
  }

  removeRegressionTestCase(index: number): void {
    this.regressionTestCases = this.regressionTestCases.filter((_, currentIndex) => currentIndex !== index);
  }

  async saveRegressionTests(): Promise<void> {
    if (!this.assistant?.id) return;
    this.isSavingRegressionTests = true;
    try {
      const testCases = this.normalizeRegressionTestCases(this.regressionTestCases);
      const saved = await this.assistantsService.saveRegressionTestsAsync(this.assistant.id, testCases).toPromise();
      this.regressionTestCases = this.cloneRegressionTestCases(saved ?? []);
      this.assistant.regressionTestCases = this.cloneRegressionTestCases(this.regressionTestCases);
      this.snackBar.showSuccess('Regression tests saved.', 3000);
    } catch (error) {
      console.error('Error saving assistant regression tests', error);
      this.snackBar.showError('Error saving regression tests.', 3000);
    } finally {
      this.isSavingRegressionTests = false;
    }
  }

  private applyFormToAssistant(): void {
    if (!this.assistant) return;
    Object.assign(this.assistant, this.settingsForm.value);
    this.normalizeReasoningOptions();
    this.assistant.webSearchDomains = this.normalizeDomainList(this.assistant.webSearchDomains ?? []);
    this.assistant.fileRefs = this.getUnionFileRefs(this.directFileRefs, this.vectorFileRefs);
    this.assistant.directFileRefs = [...(this.directFileRefs ?? [])];
    this.assistant.vectorFileRefs = [...(this.vectorFileRefs ?? [])];
    this.assistant.regressionTestCases = this.normalizeRegressionTestCases(this.regressionTestCases);
  }

  private async refreshFeedBindingWarnings(): Promise<void> {
    if (!this.assistant?.id) {
      this.feedBindingWarnings = [];
      return;
    }

    this.isFeedBindingValidationLoading = true;
    try {
      const result = await this.assistantsService.validateFeedBindingAsync(this.assistant.id, this.assistant).toPromise();
      this.feedBindingWarnings = result?.warnings ?? [];
    } catch (error) {
      console.error('Error validating assistant product feed bindings', error);
      this.feedBindingWarnings = [];
    } finally {
      this.isFeedBindingValidationLoading = false;
    }
  }

  private buildAssistantDraftConfig(): Partial<IAssistantOptions> {
    if (!this.assistant) return {};
    return {
      name: this.assistant.name,
      description: this.assistant.description,
      enabled: this.assistant.enabled,
      model: this.assistant.model,
      temperature: this.assistant.temperature,
      reasoningEffort: this.assistant.reasoningEffort,
      reasoningSummaryVerbosity: this.assistant.reasoningSummaryVerbosity,
      retrieval: this.assistant.retrieval,
      codeInterpreter: this.assistant.codeInterpreter,
      webSearch: this.assistant.webSearch,
      webSearchDomains: this.assistant.webSearchDomains,
      usePromptCaching: this.assistant.usePromptCaching,
      functionRefs: this.assistant.functionRefs,
      productFeedRefs: this.assistant.productFeedRefs,
      fileRefs: this.assistant.fileRefs,
      directFileRefs: this.assistant.directFileRefs,
      vectorFileRefs: this.assistant.vectorFileRefs,
      regressionTestCases: this.assistant.regressionTestCases
    };
  }

  private cloneRegressionTestCases(testCases: AssistantRegressionTestCase[]): AssistantRegressionTestCase[] {
    return testCases.map(testCase => ({...testCase, enabled: testCase.enabled ?? true}));
  }

  private normalizeRegressionTestCases(testCases: AssistantRegressionTestCase[]): AssistantRegressionTestCase[] {
    return testCases
      .filter(testCase =>
        !!testCase.name?.trim() ||
        (testCase.expectedInstructionsContains !== null && testCase.expectedInstructionsContains !== undefined) ||
        !!testCase.forbiddenInstructionsContains?.trim() ||
        !!testCase.expectedModel?.trim())
      .map(testCase => ({
        name: testCase.name?.trim() ?? '',
        expectedInstructionsContains: testCase.expectedInstructionsContains ?? null,
        forbiddenInstructionsContains: testCase.forbiddenInstructionsContains?.trim() || null,
        expectedModel: testCase.expectedModel?.trim() || null,
        enabled: testCase.enabled ?? true
      }));
  }

  private async loadVerifiedDomains(): Promise<void> {
    const clientId = this.clientService.client?.id;
    if (!clientId) {
      this.verifiedDomains = [];
      return;
    }

    this.isDomainsLoading = true;
    try {
      const records = await this.domainsService.listVerifiedDomains(clientId).toPromise();
      const available = this.normalizeDomainList((records || []).map(record => record.domain));
      this.verifiedDomains = available;

      const control = this.settingsForm.get('webSearchDomains');
      if (!control) return;

      const current = this.normalizeDomainList(control.value ?? []);
      const selected = current.filter(domain => available.includes(domain));

      if (selected.length > 0) {
        control.setValue(selected, {emitEvent: false});
      } else if (available.length > 0) {
        // Default to all verified domains when nothing is explicitly selected.
        control.setValue(available, {emitEvent: false});
      }
    } catch (error) {
      console.error('Error loading verified domains', error);
      this.verifiedDomains = [];
    } finally {
      this.isDomainsLoading = false;
    }
  }

  private normalizeDomainList(values: string[] | null | undefined): string[] {
    if (!values || values.length === 0) return [];
    const seen = new Set<string>();
    const normalized: string[] = [];
    for (const value of values) {
      const domain = this.normalizeDomain(value);
      if (!domain || seen.has(domain)) continue;
      seen.add(domain);
      normalized.push(domain);
    }
    return normalized;
  }

  private normalizeDomain(value: string | null | undefined): string | null {
    if (!value) return null;
    let trimmed = value.trim().toLowerCase();
    if (!trimmed) return null;
    trimmed = trimmed.replace(/^https?:\/\//, '');
    trimmed = trimmed.replace(/^\*\./, '');
    trimmed = trimmed.replace(/^www\./, '');
    const slashIndex = trimmed.indexOf('/');
    if (slashIndex >= 0) trimmed = trimmed.slice(0, slashIndex);
    const queryIndex = trimmed.indexOf('?');
    if (queryIndex >= 0) trimmed = trimmed.slice(0, queryIndex);
    const hashIndex = trimmed.indexOf('#');
    if (hashIndex >= 0) trimmed = trimmed.slice(0, hashIndex);
    const atIndex = trimmed.lastIndexOf('@');
    if (atIndex >= 0) trimmed = trimmed.slice(atIndex + 1);
    const colonIndex = trimmed.indexOf(':');
    if (colonIndex >= 0) trimmed = trimmed.slice(0, colonIndex);
    trimmed = trimmed.replace(/\.+$/, '');
    return trimmed || null;
  }
  // Delete Assistant

  delete() {
    if (!this.assistant) return;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '350px',
      data: {
        title: 'Are you sure you want to delete this?',
        message: 'This will delete the record. Once deleted, it cannot be rolled back.',
        confirmText: 'Do you want to proceed?',
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isDeleting = true;
        this.snackBar.showInfo('Deleting assistant...', 3000);
        this.assistantsService.deleteAsync(this.assistant?.id ?? "").subscribe(() => {
          this.snackBar.showSuccess('Assistant deleted successfully.', 3000);
          this.router.navigate(['/ai/assistants']);
        }, error => {
          console.error('Error deleting assistant', error);
          this.snackBar.showError('Error deleting assistant.', 3000);
          this.isDeleting = false;
        });
      } else {
        this.snackBar.showInfo('Delete action canceled.', 3000);
      }
    });
  }

  // publish flow removed with move to Responses API
  /**
   * Returns the union of the direct and vector file references arrays, removing duplicates.
   */
  private getUnionFileRefs(direct: string[] = [], vector: string[] = []): string[] {
    return Array.from(new Set([...(direct ?? []), ...(vector ?? [])]));
  }

    private normalizeReasoningOptions(): void {
        if (!this.assistant) return;
        if (!isReasoningModel(this.assistant.model)) {
            this.assistant.reasoningEffort = undefined;
            this.assistant.reasoningSummaryVerbosity = undefined;
            return;
        }

        const allowedEfforts = getReasoningEffortOptions(this.assistant.model);
        const currentEffort = this.assistant.reasoningEffort ?? '';
        if (allowedEfforts.length && !allowedEfforts.includes(currentEffort)) {
            this.assistant.reasoningEffort = getReasoningEffortDefault(this.assistant.model);
        } else if (!this.assistant.reasoningEffort) {
            this.assistant.reasoningEffort = getReasoningEffortDefault(this.assistant.model);
        }

        if (this.assistant.reasoningSummaryVerbosity === undefined) {
            this.assistant.reasoningSummaryVerbosity = '';
        }
    }
}
